import format from "date-fns/format";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { BASE_PATH } from "../..";
import { ReactComponent as Edit } from "../../assets/icons/edit.svg";
import { AuthContext } from "../../contexts/AuthContext";
import { ProjectStatus, ProjectStatusLabels } from "../../models/enums";
import { OrderDTO, OrderlineViewDTO, TemplateService } from "../../openapi/requests";
import IssueReporting from "../layout/IssueReporting";
import "../layout/elements/Lists.scss";
import "./ProjectDetail.scss";
import { useOrderServicePostOrderOrderlines, useProjectServiceGetProject1, useProjectServiceGetProject1Key, useProjectServiceGetProjectOrders, useProjectServiceGetProjectOrdersKey, useProjectServicePutProjectDone } from "../../openapi/queries";
import Select, { StylesConfig } from "react-select";
import { Option } from "../../types/types";
import { OrderlineListV2 } from "./OrderlineListV2";
import OrderlinesUploadToDam from "./OrderlinesUploadToDam";
import { useQueryClient } from "@tanstack/react-query";

interface Props {
  projectId?: number;
}

export const ProjectDetail: React.FC<Props> = ({ projectId }) => {
  const { hasPermission } = useContext(AuthContext);

  const navigate = useNavigate();
  const [files, setFiles] = useState<OrderlineViewDTO[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<OrderlineViewDTO[]>([]);
  const [order, setOrder] = useState<OrderDTO | undefined>();
  const [selectedOrder, setSelectedOrder] = useState<Option>();
  const [hasMoodboard, setHasMoodboard] = useState(false);
  const [projectFilesOrdered, setProjectFilesOrdered] = useState(0);
  const [projectFilesProduced, setProjectFilesProduced] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [localOrderlines, setLocalOrderLines] = useState<OrderlineViewDTO[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedProjectFileFilter, setSelectedProjectFileFilter] = useState<string>("");

  const [showOrderlineUpload, setShowOrderlineUpload] = useState<boolean>(false);
 
  const queryClient = useQueryClient();

  const {
    data: project,
    isFetched: projectFetched,   
  } = useProjectServiceGetProject1({ key: projectId ?? 0 }, [useProjectServiceGetProject1Key, projectId], { refetchOnWindowFocus: false });
  const {
    data: orders,
    isFetched: projectOrdersFetched,   
  } = useProjectServiceGetProjectOrders({ key: project?.id ?? 0 }, [useProjectServiceGetProjectOrdersKey, project?.id], {
    enabled: projectFetched,
    refetchOnWindowFocus: false,
  });

  const { mutateAsync: markDone } = useProjectServicePutProjectDone();

  const sortedOrders: OrderDTO[] | undefined = useMemo(() => {
    if (projectOrdersFetched) {
      return orders.sort((a, b) => {
        return new Date(b.timestampCreate).getTime() - new Date(a.timestampCreate).getTime();
      });
    }
    return [];
  }, [orders, projectOrdersFetched]);

  const { mutateAsync: getOrderlines } = useOrderServicePostOrderOrderlines({});

  useEffect(() => {
    const fetchOrderlines = async () => {
      const orderlines = await getOrderlines({ requestBody: sortedOrders.map((x) => x.id) });
      setLocalOrderLines(orderlines);
    };

    fetchOrderlines();
  }, [getOrderlines, sortedOrders]);

  const updateProjectStats = useCallback(async () => {
    if (projectOrdersFetched) {
      setFiles([]);

      if (localOrderlines.length > 0) {
        var template = await TemplateService.getTemplate1(localOrderlines[0].templateId);
        setHasMoodboard(template?.editorModule !== "cutout" && !template?.title.toLowerCase().includes("cutout"));
        setLastUpdate(localOrderlines.reduce((i, e) => (new Date(e.timestampUpdate) > i ? new Date(e.timestampUpdate) : i), new Date("1900-01-01")));

        setProjectFilesOrdered(localOrderlines.reduce((i, e) => i + e.files.length, 0));
        setProjectFilesProduced(localOrderlines.filter((x) => x.status === 20).reduce((i, e) => i + e.files.length, 0));

        if (order) {
          const files = localOrderlines.filter((x) => x.orderId === order.id);
          setFiles(files);
        }

        setLoading(false);
      }
    }
  }, [localOrderlines, order, projectOrdersFetched]);

  useEffect(() => {   
    setLoading(true);

    if (projectOrdersFetched) {
      const currentOrder = !selectedOrder ? sortedOrders[0] : sortedOrders.find((x) => x.id === parseInt(selectedOrder?.value ?? 0));
      if (currentOrder && currentOrder.id !== order?.id) {
        setSelectedOrder({ label: format(new Date(currentOrder.timestampCreate ?? "1900-01-01"), "dd.MM.yyyy - pp"), value: currentOrder.id.toString() });
        setOrder(currentOrder);
      }

      updateProjectStats();
    }
    //}
  }, [order, projectOrdersFetched, selectedOrder, sortedOrders, updateProjectStats]);

  /* const updateOrderlines = useCallback(() => {
    if (sortedOrders.length > 0) {
        
      //  currentOrder = sortedOrders.find((x) => x.id === parseInt(selectedOrder?.value ?? 0));
      //  let lines = await OrderlineService.getOrderOrderline(order.id);


    }
  },[]);*/

  /*useEffect(() => {
    setLoading(true);

    //if (projectOrdersFetched) {

    if (!order) {
      const currentOrder = sortedOrders[0];
      setOrder(currentOrder);
      setSelectedOrder({ label: format(new Date(currentOrder.timestampCreate ?? "1900-01-01"), "dd.MM.yyyy - pp"), value: currentOrder.id.toString() });
    }

    updateProjectStats();
    setLoading(false);
    // }
  }, [order, sortedOrders, updateProjectStats]);*/

  /* useEffect(() => {
    let currentOrder: OrderDTO | undefined;

    if (!order) {
      currentOrder = sortedOrders[0];
      setOrder(currentOrder);
      setSelectedOrder({ label: format(new Date(currentOrder.timestampCreate ?? "1900-01-01"), "dd.MM.yyyy - pp"), value: currentOrder.id.toString() });
    }
  }, [order, orders, selectedOrder, sortedOrders]);*/

  const onMoodboardClick = () => {
    if (project && (hasPermission("FinalRender") || hasPermission("PriorityRender") || hasPermission("PreviewRender"))) {
      navigate("/create/edit/" + project.id);
    }
  };

  const options: Array<Option> = [
    { value: "", label: "Any status" },
    { value: "preview", label: "Preview" },
    { value: "adjustment", label: "In adjustment" },
    { value: "adjustment_complete", label: "Adjustment complete" },
    { value: "final", label: "Final render" },
    { value: "approved", label: "Approved" },
    { value: "dam", label: "On Dam" },
  ];

  const sortedOrderOptions: Array<Option> = useMemo(() => {
    return sortedOrders.map((o) => ({ label: format(new Date(o?.timestampCreate ?? "1900-01-01"), "dd.MM.yyyy - pp"), value: o.id.toString() }));
  }, [sortedOrders]);

  const onProjectFileFilterChange = (e: any) => {
    setSelectedProjectFileFilter(e.value);
  };

  const onChangeHistoryOrder = async (e: any) => {
    var historyOrder = orders.find((x) => x.id === parseInt(e.value));
    if (historyOrder && historyOrder.id !== order?.id) {
      setOrder(historyOrder);
      setSelectedOrder(e);
    }
  };

  const colourStyles: StylesConfig<Option, true> = {
    control: (styles) => ({
      ...styles,
      borderRadius: "0",
      fontSize: "12px",
      minHeight: "32px",
      minWidth: "180px",
      backgroundColor: "#1A1A1A",
    }),
    singleValue: (styles) => ({
      ...styles,
      color: "#FFF",
    }),
    menu: (styles) => ({
      ...styles,
      padding: "0px",
      margin: "0px",
      backgroundColor: "#1A1A1A",
      borderRadius: "0",
      borderStyle: "solid",
      borderWidth: "1px",
      borderColor: "#FFF",
    }),
    indicatorSeparator: (styles) => ({
      ...styles,
      display: "none",
      backgroundColor: "#1A1A1A",
      color: "#1A1A1A",
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      padding: "0px 8px 0px 0px",
      color: "#FFF",
    }),
    clearIndicator: (styles) => ({
      ...styles,
      padding: "0px 0px 0px 0px",
      display: "none",
    }),
    noOptionsMessage: (styles) => ({
      ...styles,
      fontSize: "12px",
      backgroundColor: "#1A1A1A",
      color: "#FFF",
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => ({
      ...styles,
      display: "flex",
      alignItems: "center",
      fontSize: "12px",
      color: isSelected ? "#1A1A1A" : isFocused ? "#1A1A1A" : "#FFF",
      backgroundColor: isSelected ? "#FFF" : isFocused ? "#FFF" : "#1A1A1A",
      ":active": {
        ...styles[":active"],
        color: isSelected ? "#1A1A1A" : isFocused ? "#1A1A1A" : "#FFF",
        backgroundColor: isSelected ? "#FFF" : isFocused ? "#FFF" : "#1A1A1A",
      },
    }),
  };

  const onUpdated = async () => {
    setLoading(true);
    setFiles([]);
    await queryClient.invalidateQueries({ queryKey: [useProjectServiceGetProjectOrdersKey] });
    updateProjectStats();
  };

  const onMarkDone = async () => {
    if (projectId) {
      await markDone({ key: projectId });
      await queryClient.invalidateQueries({ queryKey: [useProjectServiceGetProject1Key] });
    }
  };

  const onMoveToDAM = (orderlines: OrderlineViewDTO[]) => {
    setSelectedFiles(orderlines);
    setShowOrderlineUpload(true);
  };

  return (
    <div className="project-detail">
      <div className="project-detail-header">
        <h1>{project?.name}</h1>
      </div>

      <div className="project-content-wrapper">
        <div>
          <div className="info-list">
            <div className="info-list-header">
              <span>Basics</span>
            </div>
            <ul>
              <li>
                <span>Name</span>
                <span>{project?.name}</span>
              </li>
              <li>
                <span>Order By</span>
                <span>{project?.modifiedUserName}</span>
              </li>
              <li>
                <span>Order Time</span>
                <span>{format(new Date(order?.timestampCreate ?? "1900-01-01"), "dd.MM.yyyy - pp")}</span>
              </li>
              <li>
                <span>Notifications</span>
                <span>{order?.notificationRecipients}</span>
              </li>
              <li>
                <span>Status</span>
                <span>{ProjectStatusLabels[(project?.status ?? 0) as ProjectStatus]}</span>
              </li>
              <li>
                <span>Last Status Update</span>
                <span>{format(lastUpdate, "dd.MM.yyyy - pp")}</span>
              </li>
              <li>
                <span>Total Files Ordered</span>
                <span>{projectFilesOrdered}</span>
              </li>
              <li>
                <span>Total Files Produced</span>
                <span>{projectFilesProduced}</span>
              </li>
            </ul>
          </div>

          {hasMoodboard && (
            <div className="info-list">
              <div className="info-list-header">Moodboard</div>
              <button className="project-info-list-img" onClick={() => onMoodboardClick()}>
                <div className="project-edit-overlay">
                  <span>
                    <Edit />
                  </span>
                </div>
                <img src={BASE_PATH + "/project/" + project?.id + "/moodboard"} alt="Edit project" />
              </button>
            </div>
          )}
        </div>

        <div className="info-list project-images-list">
          <div className="info-list-header">
            <span>Images created with this scene</span>
            <div className="project-images-list-topactions">
              <span>
                <Select options={sortedOrderOptions} placeholder="" onChange={onChangeHistoryOrder} value={selectedOrder} styles={colourStyles} />
              </span>
              <span>
                <Select options={options} placeholder="" onChange={onProjectFileFilterChange} defaultValue={options[0]} styles={colourStyles} />
              </span>
              <span>
                <IssueReporting type="project" projectId={projectId} className="link" />
                {project?.status !== 60 ? (
                  <button className="rounded" onClick={onMarkDone}>
                    Mark project as done
                  </button>
                ) : null}
              </span>
            </div>
          </div>
          {/* {loading && (
            <div className="loading">
              <Loading />
            </div>
          )} */}
          <OrderlineListV2 project={project} orders={orders} orderlines={files} filter={options.find((x) => x.value === selectedProjectFileFilter)} loading={loading} view="list" onUpdate={() => onUpdated()} onMoveToDAM={onMoveToDAM} />
          <OrderlinesUploadToDam isOpen={showOrderlineUpload} onClose={() => setShowOrderlineUpload(false)} projectOrFolder={project} orderlines={selectedFiles} />

          {/* {files?.length === 0 && !loading && (
            <div className="project-images-list-no-images">
              <span>No available images</span>
            </div>
          )} */}
          {/* <div className="project-images-list-bottomactions">
                        <button className="rounded" disabled={isDownloading} onClick={() => { onDownloadAll(project?.id ?? 0) }}>Download all {isDownloading && <Loading />}</button>
                        <button className="rounded" disabled={isDownloadingPowerpoint} onClick={onDownloadPowerpoint}>Download as powerpoint {isDownloadingPowerpoint && <Loading />}</button>
                    </div> */}
        </div>
      </div>
    </div>
  );
};
